import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { type z } from "zod";
import { useAuth, type Terms } from "@/auth/AuthProvider";
import { formSchema, type FormValues } from "@/auth/schema";
import { Heading1, Paragraph } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { getErrorMessage } from "@/lib/utils/errors";

type ErrorMessage = string;

type PendingTerms = { terms: Terms; businessUnitId: number };

function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();
  const [pendingTerms, setPendingTerms] = useState<PendingTerms | null>(null);
  const router = useRouter();
  const navigate = useNavigate({ from: "/login" });
  const { redirect } = useSearch({ from: "/(unauthed)/_layout/login" });
  const auth = useAuth();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const redirectAfterLogin = async () => {
    await router.invalidate();
    const redirectTo = redirect || "/bookings";
    await navigate({
      to: redirectTo,
      search: { redirect: undefined },
    }).catch((error: unknown) => {
      setErrorMessage(getErrorMessage(error));
    });
  };

  const onSubmit = async ({ email, password }: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      setErrorMessage(undefined);
      const result = await auth.login({ email, password });

      if (!result.success) {
        setErrorMessage(result.error);
        return;
      }

      if (result.requiresTermsAccept) {
        setPendingTerms({ terms: result.terms, businessUnitId: result.businessUnitId });
        return;
      }

      await redirectAfterLogin();
    } catch (e: unknown) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const onAcceptTerms = async () => {
    if (!pendingTerms) return;
    try {
      setIsLoading(true);
      setErrorMessage(undefined);
      const result = await auth.acceptTermsAndConditions(pendingTerms.terms.id, pendingTerms.businessUnitId);

      if (!result.success) {
        setErrorMessage(result.error);
        return;
      }

      setPendingTerms(null);
      await redirectAfterLogin();
    } catch (e: unknown) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex w-full max-w-xs flex-col items-center md:max-w-sm">
      <Form {...form}>
        <Heading1 className="text-2xl font-bold lg:text-3xl">Log in</Heading1>
        <Paragraph className="text-center text-muted-foreground">Enter your account details to log in</Paragraph>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
          <FormFields form={form} />
          <FormMessage>{errorMessage}</FormMessage>
          <Button isLoading={isLoading} type="submit">
            Log in
          </Button>
        </form>
      </Form>

      <Dialog
        open={pendingTerms !== null}
        onOpenChange={(open) => {
          // Dismissing without accepting (Escape is the only remaining path —
          // outside clicks are blocked and the close button is hidden) must
          // also end the session signIn already committed, otherwise the
          // refresh cookie + session marker would authenticate the user past
          // the T&C gate on the next reload.
          if (!open) {
            setPendingTerms(null);
            void auth.logout();
          }
        }}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          className="flex max-h-[90vh] flex-col [&>button:last-child]:hidden"
        >
          <DialogHeader>
            <DialogTitle>{pendingTerms?.terms.title ?? ""}</DialogTitle>
          </DialogHeader>
          <DialogDescription
            className="min-h-0 flex-1 overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: pendingTerms?.terms.description ?? "" }}
          />
          <DialogFooter>
            <Button type="button" onClick={onAcceptTerms} isLoading={isLoading}>
              Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormFields({ form }: { form: UseFormReturn<FormValues> }) {
  return (
    <>
      <FormField
        control={form.control}
        name="email"
        render={({ field, fieldState: { error } }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input placeholder="Email" autoComplete="email" type="email" {...field} />
            </FormControl>
            <FormMessage>{error?.message}</FormMessage>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="password"
        render={({ field, fieldState: { error } }) => (
          <FormItem>
            <FormLabel>Password</FormLabel>
            <FormControl>
              <Input placeholder="Password" type="password" autoComplete="current-password" {...field} />
            </FormControl>
            <FormMessage>{error?.message}</FormMessage>
          </FormItem>
        )}
      />
    </>
  );
}

export { LoginForm };
